import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import ProcessMonitorStatus from '@linc-technologies/linced-common/enums/process-monitor-status';
import type ProcessMonitor from '@linc-technologies/linced-common/models/cron/process-monitor';
import type StoreService from '@linc-technologies/linced-common/services/store';

type ResourceIds = StatefulArray<string>;

// The cron/process-monitor fields read while polling.
type ProcessMonitorRecord = Pick<ProcessMonitor, 'status' | 'resourceIds'>;

export async function pollLongRunningTask(
  processMonitorId: string,
  storeService: StoreService,
  maxTimeout = 40000,
): Promise<ResourceIds> {
  const interval = 1000;
  let pollingStopped = false;

  // Timeout promise
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      pollingStopped = true;
      reject(new Error('Polling the long task timed out'));
    }, maxTimeout);
  });

  // Polling promise
  const pollingPromise = new Promise<ResourceIds>((resolve, reject) => {
    const poll = async () => {
      if (pollingStopped) {
        // The timeout's been triggered, prevent recursion
        return;
      }
      try {
        const response = (await storeService.findRecord(
          'cron/process-monitor',
          processMonitorId,
        )) as ProcessMonitorRecord;
        switch (response.status) {
          case ProcessMonitorStatus.COMPLETE:
            return resolve(response.resourceIds);
          case ProcessMonitorStatus.FAILED:
            return reject(new Error('Process failed'));
          case ProcessMonitorStatus.PROCESSING:
          case ProcessMonitorStatus.PENDING:
            setTimeout(() => void poll(), interval);
            break;
          default:
            return reject(new Error(`Unexpected status: ${response.status}`));
        }
      } catch (error) {
        // Pass on whatever findRecord rejected with.
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(error);
      }
    };

    void poll();
  });

  // Race the polling promise against the timeout promise
  return Promise.race([pollingPromise, timeoutPromise]);
}
